import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type GeneralDocumentAccessJSON = z.infer<typeof GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ>;

export type GeneralDocumentAccessParams = z.infer<typeof GeneralDocumentAccessSchema.GeneralDocumentAccessZ>;

export default class GeneralDocumentAccessSchema {
  public static readonly GeneralDocumentAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.GeneralDocumentAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly GeneralDocumentAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.GeneralDocumentAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): GeneralDocumentAccessParams {
    return parseOrThrow(GeneralDocumentAccessSchema.GeneralDocumentAccessZ, values, {
      logError: true,
      schemaName: "GeneralDocumentAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): GeneralDocumentAccessJSON {
    return parseOrThrow(GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ, values, {
      logError: true,
      schemaName: "GeneralDocumentAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is GeneralDocumentAccessParams {
    try {
      GeneralDocumentAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is GeneralDocumentAccessJSON {
    try {
      GeneralDocumentAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: GeneralDocumentAccessJSON): GeneralDocumentAccessParams {
    const parsed = parseOrThrow(GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ, data, {
      logError: true,
      schemaName: "GeneralDocumentAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return GeneralDocumentAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: GeneralDocumentAccessParams): GeneralDocumentAccessJSON {
    const parsed = GeneralDocumentAccessSchema.validate(notification);
    return GeneralDocumentAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ,
    GeneralDocumentAccessSchema.GeneralDocumentAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.GeneralDocumentAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.GeneralDocumentAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
